import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  CommunicationTemplate,
  CommunicationTemplateEvent,
  CommunicationTemplateEventHumanized,
} from '../../../core/models/entities/communication-template';
import { InputOption } from '../../../core/models/common/input-option';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { ApiError } from '../../../core/models/common/api-error';
import { CommunicationTemplatesService } from '../../../core/services/communication-templates.service';
import { Store } from '@ngrx/store';
import { communicationTemplatesActions } from 'angular/app/core/store/communication-templates';
import { ToastService } from '../../../core/services/toast.service';

@Component({
  selector: 'app-communication-template-form',
  template: `
    <form>
      <app-text-input
        label="Titre du modèle"
        name="name"
        [control]="form.get('name')"
      ></app-text-input>
    
      <app-rich-text-input
        label="Communication"
        name="message"
        [control]="form.get('message')"
      ></app-rich-text-input>
    
      <div class="mb-2 font-medium">Proposer ce modèle lors...</div>
      <div class="flex flex-wrap gap-x-6">
        @for (formGroup of suggestedWhenFormGroups; track formGroup) {
          <app-single-checkbox-input
            [label]="formGroup.get('label').value"
            name="suggest_when_{{ formGroup.get('value').value }}"
            [control]="formGroup.get('selected')"
          ></app-single-checkbox-input>
        }
      </div>
    
      <div class="my-8 flex items-center justify-end gap-x-6">
        <app-button
          data-cy="cancel-button"
          size="sm"
          (click)="onCancel.emit()"
          variant="link"
          >Annuler
        </app-button>
        <app-button
          data-cy="save-button"
          icon="check"
          size="sm"
          (click)="submit()"
          variant="secondary"
          [loading]="sendingData"
          >Valider
        </app-button>
      </div>
    </form>
    `,
  standalone: false,
})
export class CommunicationTemplateFormComponent implements OnInit {
  @Input() communicationTemplate: CommunicationTemplate;
  @Output() onCancel = new EventEmitter<void>();
  @Output() communicationTemplateSaved =
    new EventEmitter<CommunicationTemplate>();
  form: UntypedFormGroup;
  sendingData = false;
  suggestedWhenOptions = Object.values(CommunicationTemplateEvent).map(
    (event) => ({
      value: event,
      label: CommunicationTemplateEventHumanized[event],
    }),
  );

  constructor(
    private formValidationService: FormValidationService,
    private communicationTemplatesService: CommunicationTemplatesService,
    private store: Store,
    private toastService: ToastService,
  ) {}

  ngOnInit() {
    this.form = new UntypedFormGroup({
      name: new UntypedFormControl(this.communicationTemplate?.name ?? '', [
        Validators.required,
      ]),
      message: new UntypedFormControl(
        this.communicationTemplate?.message ?? '',
        [Validators.required],
      ),
      suggest_when: new UntypedFormArray(
        this.suggestedWhenOptions.map(
          (option: InputOption) =>
            new UntypedFormGroup({
              label: new UntypedFormControl(option.label),
              value: new UntypedFormControl(option.value),
              selected: new UntypedFormControl(
                this.communicationTemplate?.suggest_when?.includes(
                  option.value as string,
                ),
              ),
            }),
        ),
      ),
    });
  }

  get suggestedWhenFormGroups(): UntypedFormGroup[] {
    return (this.form.get('suggest_when') as UntypedFormArray)
      .controls as UntypedFormGroup[];
  }

  async submit() {
    if (!this.formValidationService.validate(this.form)) return;

    try {
      this.sendingData = true;
      let communicationTemplate: CommunicationTemplate;
      const data = {
        ...this.form.value,
        suggest_when: this.form.value.suggest_when
          .filter((value) => value.selected)
          .map((value) => value.value),
      };
      if (this.communicationTemplate) {
        communicationTemplate = await this.communicationTemplatesService.update(
          this.communicationTemplate.id,
          data,
        );
      } else {
        communicationTemplate =
          await this.communicationTemplatesService.create(data);
      }
      this.store.dispatch(
        communicationTemplatesActions.upsertOne({ communicationTemplate }),
      );
      this.communicationTemplateSaved.emit(communicationTemplate);
      this.toastService.success(
        'Le modèle de communication a été enregistré avec succès.',
      );
    } catch (exception) {
      this.formValidationService.displayApiValidationErrors(
        this.form,
        exception as ApiError,
      );
    }
    this.sendingData = false;
  }
}
